import type { ComponentProps } from 'react';

import { LocationPinIcon } from '@change/ds-icons';

import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from '@change/ds-core-ui/next';

import { Translate, useI18n } from '@change-corgi/core/react/i18n';

import { ComboBox } from './components/ComboBox';
import { useEditLocationModal } from './hooks/useEditLocationModal';

type EditLocationModalProps = Required<Pick<ComponentProps<typeof Modal>, 'trigger'>> &
	Pick<ComponentProps<typeof Modal>, 'defaultOpen' | 'onClose'>;

export const EditLocationModal: React.FC<EditLocationModalProps> = ({ trigger, onClose, defaultOpen }) => {
	const { translate } = useI18n();
	const { data, actions } = useEditLocationModal({ closeModal: onClose });

	return (
		<Modal
			defaultOpen={defaultOpen}
			trigger={trigger}
			size="large"
			className="overflow-y-auto [&>div>div]:!max-h-[initial] [&>div>div]:flex-[1_0_auto] [&>div>div]:md:!max-h-[700px] [&_section]:h-full"
		>
			<ModalHeader
				title={translate('corgi.homepage.local_section.modal.header.title')}
				subtitle={translate('corgi.homepage.local_section.modal.header.subtitle')}
			/>
			<ModalBody className="!max-h-[initial] flex-[1_0_auto] px-1 md:min-h-[318px]">
				{({ close }) => <EditLocationModalBody close={close} data={data} actions={actions} />}
			</ModalBody>
			<ModalFooter>
				<Button variant="primary" onClick={actions.handleLocationSubmit} data-qa="edit-location-modal-primary-action">
					<Translate value="corgi.homepage.local_section.modal.footer.primary_action" />
				</Button>
			</ModalFooter>
		</Modal>
	);
};

const EditLocationModalBody: React.FC<{
	close: () => void;
	data: ReturnType<typeof useEditLocationModal>['data'];
	actions: ReturnType<typeof useEditLocationModal>['actions'];
}> = ({ close: _close, data, actions }) => {
	const { translate } = useI18n();

	return (
		<ComboBox
			id="petition-place"
			name="petition-place"
			label={translate('corgi.homepage.local_section.modal.body.search_input.label')}
			inputValue={data.placeInputValue}
			onInputChange={(_, newValue) => actions.handlePlaceInputChange(newValue)}
			value={data.placeValue}
			onChange={(_, newValue) => actions.handlePlaceChange(newValue)}
			options={data.places}
			filterOptions={() => data.places}
			optionToString={(place) => place.name}
			error={data.formError}
			renderOption={(option) => (
				<div className="flex items-center space-x-2">
					<LocationPinIcon className="text-content-tertiary-tertiary" />
					<span>{option.name}</span>
				</div>
			)}
			data-qa="edit-location-modal-combobox"
		/>
	);
};
